<template>
  <SkeleLoader
    v-if="isLoading"
    :lines="9"
    :hasContentImg="false"
    contentIsFullWidth
  />

  <div class="summary-table" v-else>
    <h2>{{ $t("SummaryTable.label.title") }}{{ summaryData.orderRef }}</h2>
    <ul>
      <li v-if="summaryData.amountPaid">
        <p>{{ $t('SummaryTable.ul.list3') }}</p>
        <p v-if="isABuyOrder">
          {{ toCurrencyFormat(summaryData.amountPaid, summaryData.fiatType) }}
          {{ summaryData.fiatType }}
        </p>
        <p v-else>{{ convertCoinToDecimals }} {{ summaryData.coinType }}</p>
      </li>
      <li v-if="summaryData.amountReceived">
        <p>{{ $t('SummaryTable.ul.list4') }}</p>
        <p v-if="isABuyOrder">
          {{ convertCoinToDecimals }} {{ summaryData.coinType }}
        </p>
        <p v-else>
          {{ toCurrencyFormat(summaryData.amountPaid, summaryData.fiatType) }}
          {{ summaryData.fiatType }}
        </p>
      </li>
      <li v-if="summaryData.walletAddress && isABuyOrder">
        <p>{{ $t('SummaryTable.ul.list5') }}</p>
        <p>{{ obfuscateWithMiddleEllipsis(summaryData.walletAddress) }}</p>
      </li>
      <li v-if="summaryData.blockchain && summaryData.blockchain.description">
        <p>{{ $t("SummaryTable.ul.list6") }}</p>
        <p>{{ summaryData.blockchain.description }}</p>
      </li>
      <li v-if="summaryData.paymentMethod">
        <p v-if="isABuyOrder">{{ $t('SummaryTable.ul.list7') }}</p>
        <p v-else>{{ $t('SummaryTable.ul.list8') }}</p>
        <p>{{ summaryData.paymentMethod }}</p>
      </li>
      <li v-if="filled(summaryData.networkFee)">
        <p>{{ $t("SummaryTable.ul.list9") }}</p>
        <p>
          {{ toCurrencyFormat(summaryData.networkFee, summaryData.fiatType) }}
          {{ summaryData.fiatType }}
        </p>
      </li>
      <li v-if="filled(summaryData.gatewayFee)">
        <p>{{ $t("SummaryTable.ul.list10") }}</p>
        <p>
          {{ summaryData.gatewayFeePercent }}
          {{ toCurrencyFormat(summaryData.gatewayFee, summaryData.fiatType) }}
          {{ summaryData.fiatType }}
        </p>
      </li>
      <li v-if="filled(summaryData.commissionFee)">
        <p>{{ $t("SummaryTable.ul.list11") }}</p>
        <p>
          {{ summaryData.commissionFeePercent }}
          {{
            toCurrencyFormat(summaryData.commissionFee, summaryData.fiatType)
          }}
          {{ summaryData.fiatType }}
        </p>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { computed } from "vue";
import useDecimalHelpers from "@/composables/helpers/useDecimalHelpers";
import {
  obfuscateWithMiddleEllipsis,
  toCurrencyFormat,
} from "@/composables/helpers/useStringHelpers";
import { filled } from "@/composables/helpers/useUtility";
import SkeleLoader from "@/components/ui/loaders/SkeleLoader.vue";

const props = defineProps({
  summaryData: {
    type: Object,
  },
  isLoading: {
    type: Boolean,
    default: true,
  },
});

const { convertCoinAmountToDecimals } = useDecimalHelpers;
const convertCoinToDecimals = computed(
  () =>
    props.summaryData &&
    convertCoinAmountToDecimals(props.summaryData.amountReceived, 8)
);

const isABuyOrder = computed(() => props.summaryData.orderType !== "sell");
</script>

<style lang="scss">
.summary-table {
  background: rgba(255, 255, 255, 0.08);
  padding: 2rem;
  margin-bottom: 3.4rem;
  border-radius: 1.6rem;

  h2 {
    font-size: 1.8rem;
    text-align: center;
    margin-bottom: 3.2rem;
    font-weight: 800;
    color: var(--secondary);
  }

  ul {
    width: 100%;

    li {
      width: 100%;
      display: flex;
      justify-content: space-between;
      flex-direction: column;

      @include breakpoint($min-xs) {
        flex-direction: row;
      }

      p {
        text-align: center;

        &:first-child {
          opacity: 0.7;
        }
        &:last-child {
          font-weight: 800;
        }
      }

      &:not(:last-of-type) {
        margin-bottom: 2.8rem;
      }
    }
  }
}
</style>
